/**
 * Contains type guards specifically targeting Sitecore component or data templates
 */
import { Item } from '@sitecore-jss/sitecore-jss-nextjs';

import { Core } from '.generated/templates/Foundation.Core.model';
import { Data } from '.generated/templates/Feature.Data.model';
import { Components } from '.generated/templates/Feature.Components.model';
import { Routes } from '.generated/templates/Project.Routes.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { isGenericObjectType, TypeOf } from 'lib/typescript';
import { ComponentFolders, FieldsWithChildren } from './_component-with-children';

export const isComponentWithChildren = <
  T,
  TChild extends Item | Core.BaseTemplates.Data.Fields.BaseData = Item
>(
  check: unknown,
  isChildMatch?: (c: unknown) => boolean,
  options?: {
    fieldsToCheck?: [keyof T, TypeOf<T[keyof T]>][];
    everyChild?: boolean;
    canBeEmpty?: boolean;
  }
): check is FieldsWithChildren<T, TChild> => {
  const { fieldsToCheck = [], canBeEmpty = true, everyChild = false } = options ?? {};

  if (fieldsToCheck.length > 0 && !isGenericObjectType<T>(check, fieldsToCheck)) {
    return false;
  }
  if (!isGenericObjectType<{ children: Array<unknown> }>(check, [['children', 'array']])) {
    return false;
  }
  if (check.children.length === 0) {
    return canBeEmpty;
  }
  const childPredicate = isChildMatch ?? (() => true);
  return everyChild ? check.children.every(childPredicate) : childPredicate(check.children.at(0));
};

export const isItemEx = (check: unknown): check is Sitecore.Override.ItemEx =>
  isGenericObjectType<Sitecore.Override.ItemEx>(check, [
    ['fields', 'object'],
    ['name', 'string'],
    ['id', 'string'],
  ]);

/**
 * Type guard for Table Column
 * @param check object to evaluate
 * @returns {check is Data.Table.Fields.Column} true if object is a Table Column
 */
export const isTableColumn = (check: unknown): check is Data.Table.Fields.Column & { id: string } =>
  isGenericObjectType<Data.Table.Fields.Column & { id: string }>(check, [
    ['fields', 'object'],
    ['id', 'string'],
  ]) &&
  isGenericObjectType<Sitecore.Override.ItemFields<Data.Table.Fields.Column>>(check.fields, [
    ['columnLabel', 'object'],
    ['columnSpan', 'object'],
    ['sortable', 'object'],
  ]);

/**
 * Type guard for Table Column
 * @param check object to evaluate
 * @returns {check is Data.Table.Fields.Column} true if object is a Table Column
 */
export const isTableRow = (check: unknown): check is Data.Table.Fields.Row & { id: string } =>
  isGenericObjectType<Data.Table.Fields.Row & { id: string }>(check, [
    ['fields', 'object'],
    ['id', 'string'],
  ]) &&
  isGenericObjectType<Sitecore.Override.ItemFields<Data.Table.Fields.Row>>(check.fields, [
    ['column1', 'object'],
    ['column2', 'object'],
    ['column3', 'object'],
    ['column4', 'object'],
    ['column5', 'object'],
  ]);

/**
 * Type guard for Table Column Folder
 * @param check object to evaluate
 * @returns {check is FieldsWithChildren<Components.General.Table.Fields.TableColumnFolder, Data.Table.Fields.Column>} true if object is a Table Column Folder
 */
export const isTableColumnFolder = (
  check: unknown
): check is ComponentFolders.General.Table.TableColumnFolder =>
  isComponentWithChildren(check, isTableColumn);

/**
 * Type guard for Table Row Folder
 * @param check object to evaluate
 * @returns {check is FieldsWithChildren<Components.General.Table.Fields.TableColumnFolder, Data.Table.Fields.TableRowFolder>} true if object is a Table Row Folder
 */
export const isTableRowFolder = (
  check: unknown
): check is ComponentFolders.General.Table.TableRowFolder =>
  isComponentWithChildren(check, isTableRow);

export const isTable = (check: unknown): check is ComponentFolders.General.Table.Table =>
  isComponentWithChildren(check, (c) => isTableRowFolder(c) || isTableColumnFolder(c), {
    everyChild: true,
  });
/**
 * Callout card type guard
 * @param check
 * @returns
 */
export const isCalloutCard = (
  check: unknown
): check is Components.General.CalloutCard.Fields.CalloutCard =>
  isGenericObjectType<Components.General.CalloutCard.Fields.CalloutCard>(check, [
    ['fields', 'object'],
  ]) &&
  isGenericObjectType<
    Sitecore.Override.ItemFields<Components.General.CalloutCard.Fields.CalloutCard>
  >(check.fields, [
    ['description', 'object'],
    ['headingLevel', 'object'],
    ['headingText', 'object'],
    ['image', 'object'],
    ['cta1', 'object'],
    ['cta2', 'object'],
  ]);

type ArticlePage = Sitecore.Override.ItemEx & Routes.Fields.ArticlePage;
type GenericPage = Sitecore.Override.ItemEx & Routes.Fields.GenericPage;
type EventPage = Sitecore.Override.ItemEx & Routes.Fields.EventPage;
type Banner = Sitecore.Override.ItemEx & Components.General.Banner.Fields.Banner;

export const isGenericPage = (check: unknown): check is GenericPage =>
  isItemEx(check) &&
  'url' in check &&
  isGenericObjectType<Sitecore.Override.ItemFields<Routes.Fields.GenericPage>>(
    check.fields,
    [
      ['pageTitle', 'object'],
      ['navigationTitle', 'object'],
      ['excludeFromNavigation', 'object'],
      ['pageBanner', 'object'],
      ['pageDescription', 'object'],
    ],
    false
  );

/**
 * Article page card type guard
 * @param check
 * @returns
 */
export const isArticlePage = (check: unknown): check is ArticlePage =>
  isGenericPage(check) &&
  isGenericObjectType<Sitecore.Override.ItemFields<ArticlePage>>(check.fields, [
    ['author', 'object'],
  ]);
/**
 * Event page card type guard
 * @param check
 * @returns
 */
export const isEventPage = (check: unknown): check is EventPage =>
  isGenericPage(check) &&
  isGenericObjectType<Sitecore.Override.ItemFields<EventPage>>(check.fields, [
    ['eventCategory', 'object'],
    ['startDateTime', 'object'],
    ['endDateTime', 'object'],
    ['location', 'object'],
  ]);
export const isVideoItem = (check: unknown): check is Data.Video.Fields.VideoItem =>
  isItemEx(check) &&
  isGenericObjectType<Sitecore.Override.ItemFields<Data.Video.Fields.VideoItem>>(check.fields, [
    ['cta', 'object'],
    ['videoDescription', 'object'],
    ['videoEmbedUrl', 'object'],
    ['videoLinkUrl', 'object'],
  ]);

export const isBanner = (check: unknown): check is Banner =>
  isItemEx(check) &&
  isGenericObjectType<Sitecore.Override.ItemFields<Banner>>(check.fields, [
    ['textAlignment', 'object'],
    ['cta2', 'object'],
    ['cta1', 'object'],
    ['headingLevel', 'object'],
  ]);
